import {
  ADMIN_ROLE_PERMISSIONS,
  ADMIN_ROLES,
  ADMIN_STORAGE_KEYS,
} from 'constant/admin.config';

/**
 * Admin authentication helpers.
 *
 * Synchronous localStorage backed session management for the admin module,
 * working dynamically with the user details returned by the login API.
 */

export const getAdminSession = () => {
  try {
    const raw = localStorage.getItem(ADMIN_STORAGE_KEYS.session);
    if (raw) {
      return JSON.parse(raw);
    }
    const userDetailRaw = localStorage.getItem('userDetail');
    if (userDetailRaw) {
      const userDetail = JSON.parse(userDetailRaw);
      const userTypeVal =
        userDetail?.primaryUserType ||
        (Array.isArray(userDetail?.userType)
          ? userDetail?.userType[0]?.userType || userDetail?.userType[0]
          : userDetail?.userType);

      if (userTypeVal === 'Admin') {
        return {
          adminId: userDetail?.userId || 'ADM-001',
          userName: userDetail?.userName || 'Admin',
          userSurName: userDetail?.userSurName || '',
          userEmail: userDetail?.userEmail || '',
          adminRole: userDetail?.adminRole || ADMIN_ROLES.ADMIN,
          ...userDetail,
        };
      }
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const isAdminAuthenticated = () => {
  const isAdminLogin = localStorage.getItem(ADMIN_STORAGE_KEYS.isLogin) === 'true';
  const isGeneralLogin = localStorage.getItem('isLogin') === 'true';

  if (isAdminLogin && Boolean(getAdminSession()?.adminId)) {
    return true;
  }

  if (isGeneralLogin) {
    const session = getAdminSession();
    return Boolean(session?.adminId);
  }

  return false;
};

export const getAdminPermissions = (session = getAdminSession()) => {
  if (!session) {
    return [];
  }
  return ADMIN_ROLE_PERMISSIONS[session.adminRole] || [];
};

export const hasAdminPermission = (permission, session = getAdminSession()) => {
  if (!permission) {
    return true;
  }
  return getAdminPermissions(session).includes(permission);
};

/**
 * Fired whenever the stored admin session changes, so anything already showing
 * it — the header's name and avatar — re-reads instead of holding the value it
 * captured on its first render.
 */
export const ADMIN_SESSION_EVENT = 'retrex:admin-session-changed';

const broadcastSessionChange = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(ADMIN_SESSION_EVENT));
  }
};

export const setAdminSession = (admin) => {
  localStorage.setItem(ADMIN_STORAGE_KEYS.isLogin, 'true');
  localStorage.setItem(ADMIN_STORAGE_KEYS.session, JSON.stringify(admin));
  broadcastSessionChange();
};

export const clearAdminSession = () => {
  localStorage.removeItem(ADMIN_STORAGE_KEYS.isLogin);
  localStorage.removeItem(ADMIN_STORAGE_KEYS.session);
  localStorage.removeItem('isLogin');
  localStorage.removeItem('userDetail');
  localStorage.removeItem('primaryUser');
  localStorage.removeItem('userType');
  localStorage.removeItem('token');
  localStorage.removeItem('accesstoken');
  localStorage.removeItem('moxoDetail');
  localStorage.removeItem('moxoUserId');
  broadcastSessionChange();
};

export const getAdminDisplayName = (session = getAdminSession()) => {
  if (!session) {
    return '';
  }
  return [session.userName, session.userSurName].filter(Boolean).join(' ');
};

export const getAdminInitials = (session = getAdminSession()) => {
  if (!session) {
    return 'A';
  }
  const initials = `${session.userName?.[0] || ''}${
    session.userSurName?.[0] || ''
  }`;
  return initials.toUpperCase() || 'A';
};

